import React, { useState, useEffect } from 'react';
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import InputField from 'components/FormFields/InputField';
import APFormGroup from 'components/FormFields/APFormGroup';
import FormItem from 'components/FormFields/FormItem';
import { saveOnBoardData } from 'api/onBoarding';
import { getImageInfo, uploadImage, imageProcess } from 'api/image';
import PhotoUploader from '../PhotoCropper/ImageUploader';
import PhotoCropper from '../PhotoCropper/ImageCropper';
import { getPutFormat } from './filter';
import './styles.scss';

const imageSizeLimit = Number(process.env.REACT_APP_IMAGE_SIZE_LIMIT);
const DEFAULT_ERROR_MESSAGE = 'Please complete required fields above.';
const CHECK_USERNAME = gql`
  mutation checkUsername($input: String!) {
    checkUsername(input: $input) {
      isUsernameExisted
    }
  }
`;

const FormStep = ({ formData }) => {
  const history = useHistory();
  const [checkUsername] = useMutation(CHECK_USERNAME);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userName, setUserName] = useState('');
  const [originalImg, setOriginalImage] = useState(null);
  const [uploadingImg, setUploadingImage] = useState(null);
  const [profileImageUrl, setProfileImage] = useState(null);
  const [profileImageKey, setProfileImageKey] = useState(null);
  const [imageName, setImageName] = useState('');

  const [openImageCropper, setImageCropperOpen] = useState(false);
  const [isImgInvalid, setImgInvalid] = useState(false);
  const [errorTypes, setErrorTypes] = useState(null);

  const [isFormFilled, setFormFilled] = useState(true);
  const [isUsernameInValid, setUsernameValidation] = useState(false);
  const [isSaveFailed, setSaveFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [imgLoadStatus, setImageLoadStatus] = useState('');

  useEffect(() => {
    const { userInfo } = formData;
    if (userInfo) {
      setFirstName(userInfo.firstName || '');
      setLastName(userInfo.lastName || '');
      setUserName(userInfo.userName || '');
      setProfileImage(userInfo.profileImageUrl || null);
      setProfileImageKey(userInfo.profileImageKey);
    }
  }, [formData]);

  useEffect(() => {
    if (firstName && lastName && userName) setErrorMessage('');
  }, [firstName, lastName, userName]);

  useEffect(() => {
    let timer;
    if (imgLoadStatus === 'success') {
      timer = setTimeout(() => {
        setImageLoadStatus('');
      }, 1000);
    }

    return () => clearTimeout(timer);
  }, [imgLoadStatus]);

  const onDropFile = (acceptedPhoto, filename) => {
    setImageCropperOpen(true);
    setImageName(filename);
    setUploadingImage(acceptedPhoto);
  };

  const onSaveProfileImage = async (
    fullImg,
    croppedImg,
    imageBinary,
    hashcode,
  ) => {
    setOriginalImage(fullImg);
    setUploadingImage(null);
    setProfileImage(croppedImg);

    // Validation of the image info
    const infoResponse = await getImageInfo({
      path: 'profile_images',
      header: hashcode,
      filename: imageName,
    });
    const { status, data } = infoResponse;

    if (status === 200) {
      setImageLoadStatus('loading');
      // Image Uploading to S3 bucket
      const url = data.form.action;
      const { fields } = data.form;
      const uploadResponse = await uploadImage(url, fields, imageBinary);

      if (uploadResponse.status === 204) {
        // Send the image upload status value
        const response = await imageProcess({
          path: 'profile_image',
          key: uploadResponse.key,
          method: 'post',
        });
        if (response.status === 200) {
          setImageLoadStatus('success');
          setProfileImageKey(uploadResponse.key);
        } else {
          setImageLoadStatus('failed');
        }
      } else {
        setImageLoadStatus('failed');
      }
    } else {
      setImageLoadStatus('failed');
    }
  };

  const onRemoveProfileImage = () => {
    setProfileImage(null);
    setOriginalImage(null);
    setProfileImageKey('null');
  };

  const isFormFieldsValid = async () => {
    let isFormValid1 = false;
    if (!firstName || !lastName || !userName) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    } else {
      isFormValid1 = true;
    }
    setFormFilled(isFormValid1);
    try {
      const response = await checkUsername({
        variables: {
          input: userName,
        },
      });
      const isValid = response?.data?.checkUsername?.isUsernameExisted;
      if (isValid)
        setErrorMessage(
          'The username is already in use. Please choose another.',
        );
      setUsernameValidation(isValid);
      return { isFormValid1, isFormValid2: isValid };
    } catch (e) {
      return { isFormValid1, isFormValid2: false };
    }
  };

  const saveForms = async () => {
    const newFormData = formData;
    newFormData.userInfo.firstName = firstName;
    newFormData.userInfo.lastName = lastName;
    newFormData.userInfo.userName = userName;
    newFormData.userInfo.profileImageUrl = profileImageUrl;
    newFormData.userInfo.profileImageKey = profileImageKey;

    const result = await saveOnBoardData({ boarding: getPutFormat(newFormData) });
    return result;
  };

  const handleNext = async () => {
    const { isFormValid1, isFormValid2 } = await isFormFieldsValid();
    if (isFormValid1 && !isFormValid2) {
      setSaveFailed(false);
      const response = await saveForms();
      if (response) {
        setImageLoadStatus('');
        history.push('/app/v2/account-setup/step/2');
      } else {
        setSaveFailed(true);
      }
    }
  };

  const imageUploadErrorMessage = () => {
    const { acceptType, maxFileSize } = errorTypes || {};
    if (acceptType) {
      return 'Please upload your image in one of the following formats: JPEG or PNG.';
    }
    if (maxFileSize) {
      return `Sorry, the image exceeds the maximum allowed size (${imageSizeLimit} MB).`;
    }
    return 'Something went wrong with uploading your image. Please try again.';
  };

  return (
    <>
      <PhotoCropper
        src={uploadingImg || originalImg}
        open={openImageCropper}
        onClose={() => {
          setImageCropperOpen(false);
        }}
        onSave={onSaveProfileImage}
        sizeLimit={imageSizeLimit}
      />
      <section className="stepflow_content">
        <article className="stepflow_form">
          <section className="stepflow_form-header">
            <h3 className="form-title">Let’s set up your user profile.</h3>
          </section>
          <APFormGroup
            verticalGap={0}
            gridGap={1}
          >
            <InputField
              name="first_name"
              label="FIRST NAME *"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
              showError={!isFormFilled && !firstName}
            />
            <InputField
              name="last_name"
              label="LAST NAME *"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
              showError={!isFormFilled && !lastName}
            />
          </APFormGroup>
          <APFormGroup
            verticalGap={0}
          >
            <InputField
              name="username"
              label="Username"
              sublabel="Your username is the perfect login shortcut. Customize yours here!"
              value={userName}
              required
              onChange={(e) => {
                setUserName(e.target.value);
              }}
              showError={(!isFormFilled && !userName) || isUsernameInValid}
            />
          </APFormGroup>
          <APFormGroup
            verticalGap={0}
            direction="vertical"
          >
            <FormItem
              id="profile-image"
              name="Profile image"
              type="image-upload"
              sublabel={
                `A personal profile photo allows other users in your account to
                easily identify you when it comes to in-app comments and
                notifications.`
              }
            >
              <div className="stepflow_form__uploader">
                <PhotoUploader
                  src={profileImageUrl}
                  handleOnDrop={onDropFile}
                  handleDelete={onRemoveProfileImage}
                  setImageError={(value) => setImgInvalid(value)}
                  setErrorTypes={(value) => setErrorTypes(value)}
                  imageUploadText='Drag your image to replace'
                  sizeLimit={imageSizeLimit}
                />
              </div>
            </FormItem>
            {profileImageUrl && originalImg && (
              <div className="mt-2 pb-1">
                <span
                  className="adjust-button"
                  onClick={() => {
                    setImageCropperOpen(true);
                  }}
                >
                  Adjust Image
                </span>
              </div>
            )}
          </APFormGroup>
        </article>
      </section>
      <section className="stepflow_footer">
        <button
          className="stepflow_footer-button"
          style={{ visibility: 'hidden' }}
          onClick={() => { }}
        >
          <BsArrowLeftShort size={20} /> Back
        </button>
        {(!isFormFilled || isUsernameInValid) && (
          <div className="stepflow_footer-error">{errorMessage}</div>
        )}
        {isImgInvalid && (
          <div className="stepflow_footer-error">
            {imageUploadErrorMessage()}
          </div>
        )}
        {!isImgInvalid && imgLoadStatus === 'loading' && (
          <div className="stepflow_footer-error">Updating Profile Image...</div>
        )}
        {!isImgInvalid && imgLoadStatus === 'success' && (
          <div className="stepflow_footer-error">
            Updated Profile Image Successfully
          </div>
        )}
        {!isImgInvalid && imgLoadStatus === 'failed' && (
          <div className="stepflow_footer-error">
            Updating Profile Image Failed
          </div>
        )}
        {isSaveFailed && (
          <div className="stepflow_footer-error">
            Oops! We’ve hit a snag! Click{' '}
            <span
              onClick={() => {
                window.location.href = `/app/pro/${formData.companyInfo?.orgId}/welcome`;
              }}
            >
              here
            </span>{' '}
            to continue.
          </div>
        )}
        <button
          className="stepflow_footer-button"
          color="primary"
          onClick={handleNext}
        >
          Next
          <BsArrowRightShort size={20} />
        </button>
      </section>
    </>
  );
};

export default FormStep;
